import { useCallback } from 'react'
import { watchApi } from './api'
import { standingsSearch, type AthleteProfile, type Standings, type StandingsQuery } from './broadcastData'
import { usePolledRead, type PolledRead } from './usePolledRead'

/** How often a board on screen is read again (the API caches each filter for 5 s, so this is at most every other build). */
export const STANDINGS_EVERY_MS = 10_000
/** How often an athlete's card on screen reads their profile again, as the map reads positions. */
export const PROFILE_EVERY_MS = 10_000

/**
 * A race's standings while a board shows them (`query` non-null) and the page is visible, read every 10 s
 * (docs/features/badger-cohost.md, Drawing). Null `query`: nothing is read.
 */
export function useStandings(eventId: string | null | undefined, query: StandingsQuery | null): PolledRead<Standings> {
  const key = eventId && query ? `${eventId}?${standingsSearch(query)}` : null
  const read = useCallback(
    async (signal: AbortSignal) => {
      const { data } = await watchApi.standings(eventId!, query!, signal)
      if (!data) throw new Error('No standings in the answer')
      return data
    },
    // The key says everything the read asks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  )
  return usePolledRead(key, read, STANDINGS_EVERY_MS)
}

/** An athlete's profile (progress, place, newest fix) while their card shows (`athleteId` non-null), every 10 s. */
export function useAthleteProfile(eventId: string | null | undefined, athleteId: string | null): PolledRead<AthleteProfile> {
  const key = eventId && athleteId ? `${eventId}/${athleteId}` : null
  const read = useCallback(
    async (signal: AbortSignal) => {
      const { data } = await watchApi.athleteProfile(eventId!, athleteId!, signal)
      if (!data) throw new Error('No athlete in the answer')
      return data
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  )
  return usePolledRead(key, read, PROFILE_EVERY_MS)
}
